import React from "react";

type LearningDomain = {
  title: string;
  seoTitle: string;
  desc: string;
  image: string;
  tag: string;
  bg: string;
  href: string;
  alt: string;
  keywords: string;
};

const learningDomains: LearningDomain[] = [
  {
    title: "School Foundation",
    seoTitle: "Class 7 to 10 Online Foundation Courses",
    desc: "Class 7, Class 8, Class 9 aur Class 10 students ke liye Maths, Science, Reasoning, NCERT concepts aur school foundation learning with live classes, notes, tests aur doubt support.",
    image:
      "https://images.unsplash.com/photo-1509062522246-3755977927d7?w=700&h=500&fit=crop&auto=format&q=80",
    tag: "Class 7-10",
    bg: "from-blue-50 to-sky-50",
    href: "/courses",
    alt: "Class 7 to 10 online foundation course for Maths Science and school exam preparation",
    keywords:
      "Class 7 online classes, Class 8 online tuition, Class 9 foundation course, Class 10 board preparation, Maths Science online coaching, school foundation course",
  },
  {
    title: "Board Exam Preparation",
    seoTitle: "Class 10 and Class 12 Board Exam Preparation",
    desc: "Class 10 aur Class 12 board exam preparation ke liye NCERT syllabus, sample papers, revision classes, previous year questions aur board-focused test practice.",
    image:
      "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=700&h=500&fit=crop&auto=format&q=80",
    tag: "Boards",
    bg: "from-amber-50 to-orange-50",
    href: "/courses",
    alt: "Class 10 and Class 12 board exam preparation with NCERT revision and sample papers",
    keywords:
      "Class 10 board exam preparation, Class 12 board exam preparation, NCERT online classes, board exam revision, sample paper practice, online board coaching",
  },
  {
    title: "JEE Preparation",
    seoTitle: "JEE Main Online Coaching for Physics Chemistry Maths",
    desc: "JEE Main preparation ke liye Physics, Chemistry aur Mathematics ki structured live classes, practice sheets, mock tests, chapter-wise tests aur expert doubt solving support.",
    image:
      "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=700&h=500&fit=crop&auto=format&q=80",
    tag: "JEE Main",
    bg: "from-purple-50 to-indigo-50",
    href: "/courses",
    alt: "JEE Main online coaching for Physics Chemistry Mathematics with mock tests",
    keywords:
      "JEE Main online coaching, IIT JEE preparation, Physics Chemistry Maths classes, JEE mock test series, JEE practice questions, online JEE course",
  },
  {
    title: "NEET Preparation",
    seoTitle: "NEET UG Online Coaching for Physics Chemistry Biology",
    desc: "NEET UG preparation ke liye Physics, Chemistry aur Biology ki NCERT-focused live classes, regular tests, performance tracking, notes aur mentor guidance.",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=700&h=500&fit=crop&auto=format&q=80",
    tag: "NEET UG",
    bg: "from-emerald-50 to-green-50",
    href: "/courses",
    alt: "NEET UG online coaching for Physics Chemistry Biology and medical entrance preparation",
    keywords:
      "NEET online coaching, NEET UG preparation, medical entrance coaching, Biology online classes, NCERT NEET course, NEET mock test series",
  },
  {
    title: "Live Batches",
    seoTitle: "Live Online Classes with Notes Tests and Doubt Solving",
    desc: "Daily live online classes, recorded lectures, class notes, assignments, performance reports aur teacher-led doubt solving support ke saath complete learning experience.",
    image:
      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=700&h=500&fit=crop&auto=format&q=80",
    tag: "Live Classes",
    bg: "from-cyan-50 to-blue-50",
    href: "/live-batches",
    alt: "Live online classes with recorded lectures notes assignments and doubt solving",
    keywords:
      "live online classes, recorded lectures, online tuition classes, doubt solving classes, study notes online, live batches for students",
  },
  {
    title: "Mock Test Series",
    seoTitle: "Online Mock Test Series for Boards JEE and NEET",
    desc: "Chapter-wise tests, full-length mock exams, score analysis, exam pattern based practice aur performance tracking ke saath smart online test series.",
    image:
      "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=700&h=500&fit=crop&auto=format&q=80",
    tag: "Practice",
    bg: "from-rose-50 to-pink-50",
    href: "/courses",
    alt: "Online mock test series for board exams JEE Main and NEET preparation",
    keywords:
      "online mock test series, JEE mock test, NEET mock test, board exam test series, chapter wise test, online practice test",
  },
];

const PopularLearningDomains: React.FC = () => {
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Popular Learning Domains - Online Courses for School, Boards, JEE and NEET",
    description:
      "Explore online courses for Class 7 to 10, Class 10 and 12 board exams, JEE Main, NEET UG, live classes and mock test series.",
    itemListElement: learningDomains.map((domain, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: domain.seoTitle,
      description: domain.desc,
      url: domain.href,
    })),
  };

  return (
    <section
      id="popular-learning-domains"
      className="relative z-10 mt-24 bg-white py-12 font-['Inter'] sm:py-14 lg:py-16"
      aria-labelledby="popular-learning-domains-heading"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(itemListSchema),
        }}
      />

      <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8 xl:px-4">
        <div className="mb-9 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
              Popular Online Courses
            </p>

            <h2
              id="popular-learning-domains-heading"
              className="mt-3 max-w-2xl text-3xl font-black leading-tight tracking-[-0.04em] text-slate-950 sm:text-4xl lg:text-[44px]"
            >
              Online Courses for Class 7 to 12, JEE Main, NEET UG and Board
              Exams
            </h2>
          </div>

          <div className="max-w-xl">
            <p className="text-base leading-7 text-slate-600">
              Mediit Academy par school foundation courses, Class 10 and Class
              12 board exam preparation, JEE Main coaching, NEET UG preparation,
              live online batches aur mock test series — sabhi programs
              structured classes, study notes, practice tests, performance
              tracking aur mentor support ke saath designed hain.
            </p>

            <a
              href="/courses"
              aria-label="View all online courses for school students JEE NEET and board exam preparation"
              className="mt-4 inline-flex rounded-xl bg-[#101827] px-6 py-3 text-sm font-black text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-slate-800"
            >
              View All Courses →
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {learningDomains.map((domain) => (
            <a
              key={domain.title}
              href={domain.href}
              aria-label={`${domain.seoTitle} - ${domain.keywords}`}
              title={domain.seoTitle}
              className={`group overflow-hidden rounded-[28px] border border-slate-100 bg-gradient-to-br ${domain.bg} shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_22px_60px_rgba(15,23,42,0.12)]`}
            >
              <article>
                <div className="p-5 sm:p-6">
                  <div className="mb-5 flex items-center justify-between">
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-slate-700 shadow-sm">
                      {domain.tag}
                    </span>

                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-900 shadow-sm transition-all group-hover:translate-x-1">
                      →
                    </span>
                  </div>

                  <h3 className="text-2xl font-black leading-tight tracking-[-0.035em] text-slate-950">
                    {domain.title}
                  </h3>

                  <p className="mt-2 text-sm font-bold text-blue-700">
                    {domain.seoTitle}
                  </p>

                  <p className="mt-3 min-h-[96px] text-sm leading-6 text-slate-600">
                    {domain.desc}
                  </p>

                  <p className="sr-only">{domain.keywords}</p>
                </div>

                <div className="relative mx-5 mb-5 h-[210px] overflow-hidden rounded-[22px] bg-white shadow-inner sm:mx-6 sm:mb-6">
                  <img
                    src={domain.image}
                    alt={domain.alt}
                    loading="lazy"
                    decoding="async"
                    width={700}
                    height={500}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/30 via-transparent to-transparent" />

                  <div className="absolute bottom-4 left-4 rounded-2xl bg-white/95 px-4 py-3 shadow-lg backdrop-blur-sm">
                    <p className="text-sm font-black text-slate-950">
                      Start Learning
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      Live classes + notes + tests
                    </p>
                  </div>
                </div>
              </article>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PopularLearningDomains;